import { useState, useEffect, useCallback, useMemo } from "react";
import { API, authFetch, useAuth } from "@/App";
import {
  BarChart3, TrendingUp, ShoppingCart, Wallet, Users, Store,
  Award, Calendar, Package, Trophy, MapPin, Target, RefreshCw,
  Filter, ArrowUpDown,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";

const PERIODS = [
  { value: "1d", label: "Last 1 day" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom range…" },
];

const GROUPS = [
  { value: "employee", label: "Per Salesman", icon: Users },
  { value: "dealer", label: "Per Dealer", icon: Store },
  { value: "district", label: "By Area (District)", icon: MapPin },
  { value: "pincode", label: "By Pincode", icon: MapPin },
  { value: "product", label: "Per Product", icon: Package },
  { value: "scheme", label: "Per Scheme", icon: Trophy },
];

const METRICS = [
  { value: "revenue", label: "Revenue" },
  { value: "orders", label: "Orders" },
  { value: "bags", label: "Bags" },
  { value: "collections", label: "Collections" },
  { value: "repeat_orders", label: "Repeat Orders" },
  { value: "scheme_claims", label: "Scheme Participation" },
];

const fmt = (n) => {
  if (!n && n !== 0) return "0";
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString("en-IN");
};
const fmtRs = (paise) => `Rs ${fmt((paise || 0) / 100)}`;

const KpiCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <Card className={`p-4 border-l-4 border-l-${color}-500 dark:bg-slate-800`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 text-${color}-500`} />
      <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </Card>
);

const SalesAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ districts: [], pincodes: [], schemes: [] });
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion } = useRegionFilter();

  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [customEnd, setCustomEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [groupBy, setGroupBy] = useState("employee");
  const [metric, setMetric] = useState("revenue");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [pincodeFilter, setPincodeFilter] = useState("all");
  const [limit, setLimit] = useState(10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ period, group_by: groupBy, metric, limit: String(limit) });
      if (districtFilter && districtFilter !== "all") qs.append("district", districtFilter);
      if (pincodeFilter && pincodeFilter !== "all") qs.append("pincode", pincodeFilter);
      if (activeRegion && activeRegion !== "ALL") qs.append("region", activeRegion);
      if (period === "custom") {
        if (customStart) qs.append("start_date", customStart);
        if (customEnd) qs.append("end_date", customEnd);
      }
      const res = await authFetch(`${API}/analytics/sales?${qs}`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [period, groupBy, metric, districtFilter, pincodeFilter, limit, customStart, customEnd, activeRegion]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/analytics/filter-options`);
        const json = await res.json();
        if (json.success) setOpts(json.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const summary = data?.summary || {};
  const leaders = data?.leaderboard || [];

  const metricLabel = METRICS.find((m) => m.value === metric)?.label || metric;
  const groupLabel = GROUPS.find((g) => g.value === groupBy)?.label || groupBy;

  const formatMetricValue = (b, m) => {
    if (m === "revenue" || m === "collections" || m === "scheme_value") return fmtRs(b[m] || 0);
    return fmt(b[m] || 0);
  };

  const maxMetric = useMemo(() => Math.max(1, ...leaders.map((b) => b[metric] || 0)), [leaders, metric]);

  return (
    <div className="space-y-6" data-testid="sales-analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Sales Analytics</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Multi-dimensional sales leaderboards, top performers & scheme participation</p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="sales-analytics-region-switcher"
          />
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-analytics">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="p-4 dark:bg-slate-800" data-testid="filter-bar">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Period</label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="filter-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map(p => <SelectItem key={p.value} value={p.value} data-testid={`opt-period-${p.value}`}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><Filter className="w-3 h-3" /> Group By</label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="filter-groupby"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GROUPS.map(g => <SelectItem key={g.value} value={g.value} data-testid={`opt-group-${g.value}`}>{g.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><ArrowUpDown className="w-3 h-3" /> Metric</label>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger data-testid="filter-metric"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METRICS.map(m => <SelectItem key={m.value} value={m.value} data-testid={`opt-metric-${m.value}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> District</label>
            <Select value={districtFilter} onValueChange={setDistrictFilter}>
              <SelectTrigger data-testid="filter-district"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Districts</SelectItem>
                {opts.districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> Pincode</label>
            <Select value={pincodeFilter} onValueChange={setPincodeFilter}>
              <SelectTrigger data-testid="filter-pincode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pincodes</SelectItem>
                {opts.pincodes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><Target className="w-3 h-3" /> Top-N</label>
            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v))}>
              <SelectTrigger data-testid="filter-limit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 50].map(n => <SelectItem key={n} value={String(n)}>Top {n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {period === "custom" && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-slate-100" data-testid="custom-range-inputs">
            <div>
              <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                max={customEnd}
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 tracking-wide flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                min={customStart}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="filter-end-date"
              />
            </div>
          </div>
        )}
      </Card>

      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard icon={ShoppingCart} label="Revenue" value={fmtRs(summary.total_revenue)} sub={`${summary.total_orders || 0} orders`} color="red" testId="kpi-revenue" />
        <KpiCard icon={Wallet} label="Collections" value={fmtRs(summary.total_collections)} sub={`${summary.total_collections_count || 0} txns`} color="emerald" testId="kpi-collections" />
        <KpiCard icon={Package} label="Bags" value={fmt(summary.total_bags)} sub="units sold" color="blue" testId="kpi-bags" />
        <KpiCard icon={Store} label="Active Dealers" value={fmt(summary.unique_dealers)} sub="with orders" color="violet" testId="kpi-dealers" />
        <KpiCard icon={Users} label="Active Salesmen" value={fmt(summary.unique_employees)} sub="with orders" color="amber" testId="kpi-salesmen" />
        <KpiCard icon={Trophy} label="Scheme Claims" value={fmt(summary.total_scheme_claims)} sub={fmtRs(summary.total_scheme_value)} color="orange" testId="kpi-schemes" />
      </div>

      {/* Leaderboard */}
      <Card className="p-5 dark:bg-slate-800" data-testid="leaderboard-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-semibold text-sm dark:text-white flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-500" />
            {groupLabel} · Ranked by {metricLabel}
          </h3>
          <Badge variant="outline" className="text-xs">
            {data?.total_groups || 0} groups · Top {leaders.length}
          </Badge>
        </div>

        {loading ? (
          <div className="py-12 text-center"><div className="w-8 h-8 spinner mx-auto" /></div>
        ) : leaders.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No data for this filter combination</p>
        ) : (
          <div className="space-y-2">
            {leaders.map((b, i) => {
              const pct = Math.round(((b[metric] || 0) / maxMetric) * 100);
              return (
                <div key={`${b.key}-${i}`} className="group p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors" data-testid={`leader-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${
                      i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-amber-700" : "bg-slate-300 text-slate-700"
                    }`} data-testid={`leader-rank-${i}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium dark:text-white truncate" data-testid={`leader-label-${i}`}>{b.label}</p>
                      {b.sub && <p className="text-[10px] text-slate-400 truncate">{b.sub}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold font-mono text-[#8B1A1A] dark:text-amber-400" data-testid={`leader-metric-${i}`}>
                        {formatMetricValue(b, metric)}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {b.orders} orders · {b.bags} bags
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${i === 0 ? "bg-amber-500" : i < 3 ? "bg-[#8B1A1A]" : "bg-slate-400"}`}
                      style={{ width: `${pct}%` }}
                      data-testid={`leader-bar-${i}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Cross-dimension quick insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 dark:bg-slate-800" data-testid="insight-repeat">
          <h4 className="text-sm font-semibold dark:text-white mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-500" /> Repeat Orders</h4>
          <p className="text-2xl font-bold font-mono text-slate-900 dark:text-white">
            {fmt(leaders.reduce((s, b) => s + (b.repeat_orders || 0), 0))}
          </p>
          <p className="text-xs text-slate-500 mt-1">Dealers who placed 2+ orders in this period</p>
        </Card>
        <Card className="p-4 dark:bg-slate-800" data-testid="insight-avg-order">
          <h4 className="text-sm font-semibold dark:text-white mb-2 flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-blue-500" /> Avg Order Value</h4>
          <p className="text-2xl font-bold font-mono text-slate-900 dark:text-white">
            {fmtRs(summary.total_orders ? summary.total_revenue / summary.total_orders : 0)}
          </p>
          <p className="text-xs text-slate-500 mt-1">Across {summary.total_orders || 0} orders in {PERIODS.find(p => p.value === period)?.label}</p>
        </Card>
        <Card className="p-4 dark:bg-slate-800" data-testid="insight-collection-ratio">
          <h4 className="text-sm font-semibold dark:text-white mb-2 flex items-center gap-1.5"><Wallet className="w-4 h-4 text-violet-500" /> Collection Ratio</h4>
          <p className="text-2xl font-bold font-mono text-slate-900 dark:text-white">
            {summary.total_revenue ? `${Math.round((summary.total_collections / summary.total_revenue) * 100)}%` : "—"}
          </p>
          <p className="text-xs text-slate-500 mt-1">Collections vs Revenue</p>
        </Card>
      </div>
    </div>
  );
};

export default SalesAnalyticsPage;
